import type { PhaseProcedurale } from "@/types/procedure";

const PHASES_ORDER: { key: PhaseProcedurale; label: string }[] = [
  { key: "DEPOSE", label: "Dépôt" },
  { key: "RECEVABLE", label: "Recevabilité" },
  { key: "TRIBUNAL_CONSTITUE", label: "Constitution" },
  { key: "INSTRUCTION_CONTRADICTOIRE", label: "Instruction" },
  { key: "DEBATS_CLOS", label: "Clôture" },
  { key: "EN_DELIBERE", label: "Délibéré" },
  { key: "SENTENCE_RENDUE", label: "Sentence" },
];

// Phases intermédiaires regroupées sous le jalon affiché.
const PHASE_ALIAS: Partial<Record<NonNullable<PhaseProcedurale>, PhaseProcedurale>> = {
  EN_RECEVABILITE: "RECEVABLE",
};

interface Props {
  currentPhase: PhaseProcedurale;
}

/**
 * Frise de progression du dossier (server-driven : pilotée par phase_procedurale).
 * IRRECEVABLE = état terminal rouge.
 */
export function PhaseFrise({ currentPhase }: Props) {
  if (currentPhase === "IRRECEVABLE") {
    return (
      <div className="w-full p-4 bg-red-50 border border-red-300 rounded-lg text-red-800 text-center font-semibold">
        Procédure terminée : dossier déclaré irrecevable
      </div>
    );
  }

  const normalized = (currentPhase && PHASE_ALIAS[currentPhase]) || currentPhase;
  const activeIndex = PHASES_ORDER.findIndex((p) => p.key === normalized);

  return (
    <div className="w-full py-4 overflow-x-auto">
      <ol className="flex items-center w-full min-w-[700px]">
        {PHASES_ORDER.map((phase, index) => {
          const isActive = index === activeIndex;
          const isCompleted = activeIndex !== -1 && index < activeIndex;

          const circle =
            "flex items-center justify-center w-8 h-8 rounded-full border-2 text-xs font-bold " +
            (isCompleted
              ? "bg-green-600 border-green-600 text-white"
              : isActive
                ? "border-blue-600 text-blue-600 bg-white"
                : "border-gray-300 text-gray-300 bg-white");

          const text =
            "ml-2 text-sm font-medium hidden sm:inline " +
            (isCompleted
              ? "text-green-700"
              : isActive
                ? "text-blue-700"
                : "text-gray-400");

          return (
            <li
              key={phase.label}
              className={`flex items-center ${index < PHASES_ORDER.length - 1 ? "flex-1" : ""}`}
            >
              <div className="flex items-center">
                <span className={circle}>{isCompleted ? "✓" : index + 1}</span>
                <span className={text}>{phase.label}</span>
              </div>
              {index < PHASES_ORDER.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${isCompleted ? "bg-green-600" : "bg-gray-200"}`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
